import React, { useState } from 'react';
import Icon from '@/components/Icon';
import SettingsForm from './SettingsForm';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Кастомная панель раздела «Телефония». Сверху — стандартная форма
 * настроек (CDR + AMI + записи), снизу — кнопка «Проверить AMI»:
 * PHP подключается к AMI с текущими сохранёнными значениями, делает
 * Login и сразу Logoff. По результату — зелёный «Соединение установлено»
 * или красная диагностическая ошибка.
 *
 * Проверяются СОХРАНЁННЫЕ значения. Если оператор поправил поля, но
 * не нажал «Сохранить», тест увидит старое — об этом подсказка.
 */
export default function TelephonySettingsPanel() {
  const can = useCan();
  const canRead = can('settings.telephony', 'read');

  if (!canRead) {
    return (
      <div className="settings-card">
        <h2>Нет доступа</h2>
        <p>У вас нет права на чтение настроек телефонии.</p>
      </div>
    );
  }

  return (
    <>
      <SettingsForm section="telephony" />
      <AmiTestBlock />
    </>
  );
}

function AmiTestBlock() {
  const can = useCan();
  const canWrite = can('settings.telephony', 'write');
  const [testing, setTesting] = useState(false);
  const [result, setResult]   = useState(null);

  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      const res = await api.post('/api/telephony/ami-test', {});
      setResult({ ok: true, ...res });
    } catch (e) {
      const msg = e instanceof ApiError
        ? (e.payload?.error?.message || e.message)
        : (e?.message || 'Не удалось проверить');
      setResult({ ok: false, error: String(msg) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="settings-card" style={{ marginTop: 16 }}>
      <div className="settings-card__head">
        <h2 className="settings-card__title">Проверка AMI</h2>
        <p className="settings-card__sub">
          Соединимся с Asterisk Manager Interface и попробуем войти под
          указанным пользователем. Звонок не инициируем. Используются
          сохранённые значения — если поправили поля выше, сначала
          нажмите «Сохранить».
        </p>
      </div>

      {result && result.ok && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(34,197,94,0.10)',
            color: 'var(--green-700)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: 6,
            fontSize: 13,
            display: 'grid', gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="check" size={14} />
            <b>Соединение установлено, авторизация прошла.</b>
          </div>
          {(result.greeting || result.host) && (
            <div style={{ color: 'var(--fg-muted)', fontSize: 12, marginLeft: 22 }}>
              {result.greeting && <>{result.greeting}<br /></>}
              {result.host && <>Хост: {result.host}, пользователь: {result.user}<br /></>}
              {result.channel && <>Канал-шаблон: <code>{result.channel}</code>, контекст: <code>{result.context}</code></>}
            </div>
          )}
        </div>
      )}
      {result && !result.ok && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.08)',
            color: 'var(--red-fg)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 6,
            fontSize: 13,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}
        >
          <Icon name="alert" size={14} />
          <div>
            <div><b>Не удалось:</b> {result.error || 'неизвестная ошибка'}</div>
            <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 12 }}>
              Подсказки:
              <ul style={{ margin: '4px 0 0 18px' }}>
                <li><b>Connection refused</b> — AMI не слушает (enabled=no в manager.conf), не на том bindaddr или порту, либо firewall.</li>
                <li><b>Authentication failed</b> — неверный user/secret, либо IP CRM не попал в <code>permit=</code> в manager.conf.</li>
                <li><b>Соединение без приветствия</b> — обычно тоже IP блокируется в <code>deny/permit</code>.</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="settings-card__foot">
        <div className="settings-card__spacer" />
        <button
          type="button"
          className="btn btn--sm btn--primary"
          onClick={runTest}
          disabled={testing || !canWrite}
          title={!canWrite ? 'Нужно право «Настройки телефонии: запись»' : undefined}
        >
          {testing ? 'Проверяем…' : 'Проверить AMI'}
        </button>
      </div>
    </div>
  );
}
